import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MdArrowBack, MdChevronRight, MdInsights } from "react-icons/md";
import Sidebar from "../components/Sidebar";
import DonutChart from "../components/quizAnalytics/DonutChart";
import EngagementLineChart from "../components/quizAnalytics/EngagementLineChart";
import { fetchQuizAnalyticsBundle } from "../utils/quizAnalytics";

function buildCurrentWeekPatientSummary(item) {
  const roundHistory = item?.roundHistory || item?.routineHistory || [];
  const currentWeek = roundHistory.find((entry) => entry.status === "current");
  const answeredCount = currentWeek?.answeredCount || 0;
  const correctCount = currentWeek?.correctCount || 0;
  const incorrectCount = currentWeek?.incorrectCount || 0;
  const totalQuestionSlots = currentWeek?.questionCount || 0;

  return {
    ...item,
    answeredCount,
    correctCount,
    incorrectCount,
    totalQuestionSlots,
    completionRatio: totalQuestionSlots > 0 ? answeredCount / totalQuestionSlots : 0,
  };
}

export default function QuizAnalysisPage() {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [patientSummaryMode, setPatientSummaryMode] = useState("current");

  useEffect(() => {
    let ignore = false;

    async function load() {
      setLoading(true);
      setPageError("");

      try {
        const payload = await fetchQuizAnalyticsBundle();
        if (!ignore) setAnalytics(payload);
      } catch (error) {
        if (!ignore) {
          setPageError(error?.message || "Die Analyse konnte nicht geladen werden.");
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    load();
    return () => {
      ignore = true;
    };
  }, []);

  const patientSummaries = analytics?.patientSummaries || [];
  const currentOccurrence = (analytics?.rounds || analytics?.occurrences || []).find(
    (occurrence) => occurrence.status === "current"
  );
  const currentWeekPatientSummaries = patientSummaries.map(buildCurrentWeekPatientSummary);
  const currentWeekTotals = currentWeekPatientSummaries.reduce(
    (acc, item) => ({
      correctCount: acc.correctCount + item.correctCount,
      incorrectCount: acc.incorrectCount + item.incorrectCount,
    }),
    {
      correctCount: 0,
      incorrectCount: 0,
    }
  );
  const displayedPatientSummaries =
    patientSummaryMode === "all"
      ? patientSummaries
      : currentWeekPatientSummaries;
  const engagementSeries = analytics?.engagementSeries || [];

  return (
    <div className="flex min-h-screen bg-[#cdeaff]">
      <Sidebar active="cognitive-exercises" />

      <main className="flex-1 px-0 pt-6 pb-0">
        <div className="h-full w-full rounded-tl-xl border border-black/10 bg-white/60 p-6 backdrop-blur">
          <div>
            <div>
              <button
                type="button"
                onClick={() => navigate("/quizzes")}
                className="inline-flex items-center gap-2 rounded-lg border border-black/10 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm transition hover:bg-[#F8FBFF]"
              >
                <MdArrowBack className="text-base" />
                Zurück zu Quizzes
              </button>

              <div className="mt-4 flex items-center gap-3">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[#EFF7FF] text-[#265DAB]">
                  <MdInsights className="text-2xl" />
                </span>
                <div>
                  <h1 className="text-3xl font-semibold text-gray-900">Analysis</h1>
                  <p className="mt-1 text-sm text-gray-600">
                    Überblick über Antwortqualität und Engagement aller Bewohner.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {loading && (
            <div className="mt-6 text-sm text-gray-700">Analyse wird geladen...</div>
          )}

          {pageError && (
            <div className="mt-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              {pageError}
            </div>
          )}

          {!loading && !pageError && (
            <>
              <div className="mt-8 grid grid-cols-1 gap-6 xl:grid-cols-[0.9fr,1.1fr]">
                <section className="rounded-3xl border border-black/10 bg-white/80 p-6">
                  <h2 className="text-xl font-semibold text-gray-900">Richtig vs. falsch</h2>
                  <p className="mt-2 text-sm text-gray-600">
                    {currentOccurrence
                      ? `Prozentualer Anteil aller korrekt und falsch beantworteten Fragen in der aktuellen Quizwoche${currentOccurrence.displayLabel ? `: ${currentOccurrence.displayLabel}` : ""}.`
                      : "Prozentualer Anteil aller korrekt und falsch beantworteten Fragen in der aktuellen Quizwoche."}
                  </p>

                  <div className="mt-6">
                    <DonutChart
                      correctCount={currentWeekTotals.correctCount}
                      incorrectCount={currentWeekTotals.incorrectCount}
                    />
                  </div>
                </section>

                <section className="rounded-3xl border border-black/10 bg-white/80 p-6">
                  <h2 className="text-xl font-semibold text-gray-900">Engagement über Quizwochen</h2>
                  <p className="mt-2 text-sm text-gray-600">
                    Das Engagement entspricht der Anzahl aller beantworteten Fragen über alle Nutzer hinweg.
                  </p>

                  <div className="mt-6">
                    <EngagementLineChart
                      points={engagementSeries}
                      emptyText="Keine Nutzung bisher."
                    />
                  </div>
                </section>
              </div>

              <section className="mt-8 rounded-3xl border border-black/10 bg-white/80 p-6">
                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Nutzerübersicht</h2>
                    <p className="mt-1 text-sm text-gray-600">
                      {patientSummaryMode === "all"
                        ? "Öffne einen Bewohner, um die persönliche Quiz-Analyse im Profil zu sehen."
                        : currentOccurrence
                          ? `Zeigt aktuell die laufende Quizwoche${currentOccurrence.displayLabel ? `: ${currentOccurrence.displayLabel}` : ""}.`
                          : "Aktuell ist keine laufende Quizwoche vorhanden."}
                    </p>
                  </div>

                  <div className="inline-flex w-full rounded-xl border border-black/10 bg-white p-1 md:w-auto">
                    <button
                      type="button"
                      onClick={() => setPatientSummaryMode("current")}
                      className={[
                        "flex-1 rounded-lg px-3 py-2 text-sm font-medium transition md:flex-none",
                        patientSummaryMode === "current"
                          ? "bg-[#EFF7FF] text-[#265DAB]"
                          : "text-gray-600 hover:bg-gray-50",
                      ].join(" ")}
                    >
                      Aktuelle Woche
                    </button>
                    <button
                      type="button"
                      onClick={() => setPatientSummaryMode("all")}
                      className={[
                        "flex-1 rounded-lg px-3 py-2 text-sm font-medium transition md:flex-none",
                        patientSummaryMode === "all"
                          ? "bg-[#EFF7FF] text-[#265DAB]"
                          : "text-gray-600 hover:bg-gray-50",
                      ].join(" ")}
                    >
                      Alle Wochen
                    </button>
                  </div>
                </div>

                {displayedPatientSummaries.length === 0 ? (
                  <div className="mt-6 rounded-2xl border border-dashed border-black/10 bg-white px-4 py-8 text-center text-sm text-gray-600">
                    Es sind noch keine Nutzer oder Quizdaten vorhanden.
                  </div>
                ) : (
                  <div className="mt-6 grid grid-cols-1 items-start gap-4 xl:grid-cols-2">
                    {displayedPatientSummaries.map((item) => (
                      <button
                        key={item.patientId}
                        type="button"
                        onClick={() => navigate(`/patients/${item.patientId}`)}
                        className="rounded-2xl border border-black/10 bg-white p-5 text-left shadow-sm transition hover:border-[#76B3FF]/70 hover:bg-[#F8FBFF]"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">{item.fullName}</h3>
                            <p className="mt-1 text-sm text-gray-600">
                              {item.answeredCount === 0
                                ? "Keine Nutzung bisher."
                                : `${item.answeredCount} beantwortete Frage${item.answeredCount === 1 ? "" : "n"}`}
                            </p>
                          </div>
                          <span className="inline-flex items-center gap-1 text-sm font-medium text-[#265DAB]">
                            Zum Profil
                            <MdChevronRight className="text-base" />
                          </span>
                        </div>

                        {item.answeredCount > 0 && (
                          <div className="mt-5 grid grid-cols-3 gap-3 text-sm">
                            <div className="rounded-xl bg-green-50 px-3 py-3 text-center text-green-700">
                              <div className="font-semibold">{item.correctCount}</div>
                              <div className="mt-1 text-xs uppercase tracking-wide">Richtig</div>
                            </div>
                            <div className="rounded-xl bg-red-50 px-3 py-3 text-center text-red-700">
                              <div className="font-semibold">{item.incorrectCount}</div>
                              <div className="mt-1 text-xs uppercase tracking-wide">Falsch</div>
                            </div>
                            <div className="rounded-xl bg-[#EFF7FF] px-3 py-3 text-center text-[#265DAB]">
                              <div className="font-semibold">
                                {Math.round(item.completionRatio * 100)}%
                              </div>
                              <div className="mt-1 text-xs uppercase tracking-wide">Abdeckung</div>
                            </div>
                          </div>
                        )}

                        <div className="mt-4">
                          <div className="flex items-center justify-between text-xs text-gray-500">
                            <span>
                              {patientSummaryMode === "all"
                                ? "Beantwortete Fragen über alle Quizwochen"
                                : "Beantwortete Fragen in der aktuellen Quizwoche"}
                            </span>
                            <span>
                              {item.answeredCount === 0
                                ? "Keine Nutzung"
                                : `${item.answeredCount}/${item.totalQuestionSlots || 0}`}
                            </span>
                          </div>
                          <div className="mt-2 h-2 rounded-full bg-gray-100">
                            <div
                              className="h-2 rounded-full bg-[#76B3FF]"
                              style={{
                                width: `${Math.round(item.completionRatio * 100)}%`,
                              }}
                            />
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
